import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';

// Import components
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import LoadingSpinner from './components/LoadingSpinner';
import ChangePasswordDialog from './components/ChangePasswordDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for authentication
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail.map((d) => d?.msg || String(d)).join(' ; ');
    }
    const url = error.config?.url || '';
    const isAuthCall = /\/auth\/(login|register|refresh)/.test(url);
    if (error.response?.status === 401 && !isAuthCall && window.location.pathname !== '/login') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);

  // Check authentication on app load
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        // Verify token with backend
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
        
        // Vérifier si l'utilisateur doit changer son mot de passe
        if (response.data.must_change_password) {
          setShowChangePasswordDialog(true);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      setIsAuthenticated(false);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (email, password, tenantId = null) => {
    try {
      // Si tenantId est fourni, utiliser l'endpoint avec tenant
      const endpoint = tenantId ? '/auth/login-with-tenant' : '/auth/login';
      const payload = tenantId 
        ? { email, password, tenant_id: tenantId }
        : { email, password };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      const { access_token, refresh_token, user: userData } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Vérifier si l'utilisateur doit changer son mot de passe
      if (userData.must_change_password) {
        setShowChangePasswordDialog(true);
        toast.warning('Vous devez changer votre mot de passe temporaire');
      } else {
        toast.success(`Bienvenue, ${userData.first_name} !`);
      }
      
      return { success: true };
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Gérer le cas de comptes multiples (code 409)
      if (error.response?.status === 409) {
        console.log('409 detected, detail:', error.response.data.detail);
        const errorData = error.response.data.detail;
        if (errorData && errorData.error === 'MULTIPLE_ACCOUNTS') {
          console.log('Multiple accounts confirmed, tenant_options:', errorData.tenant_options);
          return { 
            success: false, 
            multipleAccounts: true,
            tenantOptions: errorData.tenant_options,
            message: errorData.message
          };
        } else {
          console.log('409 but not MULTIPLE_ACCOUNTS, errorData:', errorData);
        }
      }
      
      const errorMessage = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Déconnexion réussie');
  };

  const handleRegister = async (userData, isInvitation = false) => {
    try {
      const endpoint = isInvitation ? '/auth/register-with-invitation' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, userData);
      
      const { access_token, refresh_token, user: newUser } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      setUser(newUser);
      setIsAuthenticated(true);
      
      const welcomeMessage = isInvitation 
        ? `Invitation acceptée ! Bienvenue, ${newUser.first_name} !`
        : `Compte créé avec succès ! Bienvenue, ${newUser.first_name} !`;
      
      toast.success(welcomeMessage);
      return { success: true };
      
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la création du compte';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
                <Navigate to="/dashboard" replace /> : 
                <LoginPage 
                  onLogin={handleLogin} 
                  onRegister={handleRegister}
                />
            } 
          />
          
          {/* Route d'inscription avec code */}
          <Route 
            path="/inscription/:enrollmentCode" 
            element={
              isAuthenticated ? 
                <Navigate to="/dashboard" replace /> : 
                <LoginPage 
                  onLogin={handleLogin} 
                  onRegister={handleRegister}
                />
            } 
          />
          
          <Route 
            path="/dashboard/*" 
            element={
              isAuthenticated ? 
                <Dashboard 
                  user={user} 
                  onLogout={handleLogout}
                /> : 
                <Navigate to="/login" replace />
            } 
          />
          
          <Route 
            path="/" 
            element={
              <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
            } 
          />
        </Routes>
      </BrowserRouter>
      
      <Toaster position="top-right" richColors />
      
      {/* Dialog de changement de mot de passe obligatoire */}
      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onPasswordChanged={async () => {
          setShowChangePasswordDialog(false);
          // Rafraîchir les données de l'utilisateur
          try {
            const response = await axios.get(`${API}/auth/me`);
            const updatedUser = response.data;
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            toast.success(`Bienvenue, ${updatedUser.first_name} !`);
          } catch (error) {
            console.error('Error refreshing user data:', error);
          }
        }}
      />
    </div>
  );
}

export default App;
